import { SuccessCallback } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { ITransferService } from '../entities/ITransferService';
import { ITransferRepository } from '../entities/ITransferRepository';
import { InitiateTransferResponseBody } from '../entities/InitiateTransferResponseDTO';
import { MakeTransferResponseBody } from '../entities/MakeTransferResponseDTO';
import {
  ExchangeCalculate,
  ExchangeRates,
  Forecast,
  Vop,
  InitiateTransferRequest,
  PostDocument,
  Routing,
  DeleteDocument,
  Document,
  SwiftCode,
  SwiftCity,
  SwiftBank,
  BopCode,
  VocCode,
  SwiftCountry,
  CurrencySearch,
} from '../entities';
import { RoutingResponseBody } from '../entities/RoutingResponseDTO';
import { ForecastResponseBody } from '../entities/ForecastResponseDTO';
import { ExchangeCalculateResponseBody } from '../entities/ExchangeCalculateResponseDTO';
import { ExchangeRatesResponseBody } from '../entities/ExchangeRatesResponseDTO';
import { BopCodesIdentifiers } from '../entities/BopCode';
import { DealerRateExecute } from '../entities/DealerRateExecute';
import { DealerRateExecuteResponseBody } from '../entities/DealerRateExecuteResponseDTO';
import { DealerRatesWithCalculatedTimer } from '../entities/DealerRatesResponseDTO';
import { Priorities, ValueDateResponseBody } from '../entities/ValueDateResponseDTO';
import { CurrencySearchResponseBody } from '../entities/CurrencySearchResponseDTO';
import { OrderChargesResponseBody } from '../entities/OrderChargesResponseDTO';
import { ForecastRoutingResponseBody } from '../entities/ForecastRoutingResponseDTO';
import { VopResponseBody } from '../entities/VopResponseDTO';

export class TransferServiceImpl implements ITransferService {
  itemRepo: ITransferRepository;

  constructor(ir: ITransferRepository) {
    this.itemRepo = ir;

    this.Routing = this.Routing.bind(this);
    this.Forecast = this.Forecast.bind(this);
    this.Vop = this.Vop.bind(this);
    this.Initiate = this.Initiate.bind(this);
    this.ExchangeCalculate = this.ExchangeCalculate.bind(this);
    this.ExchangeRates = this.ExchangeRates.bind(this);
    this.MakeTransfer = this.MakeTransfer.bind(this);
    this.PostDocument = this.PostDocument.bind(this);
    this.DeleteDocument = this.DeleteDocument.bind(this);
    this.GetDocuments = this.GetDocuments.bind(this);
    this.GetBankSwiftCodes = this.GetBankSwiftCodes.bind(this);
    this.GetSwiftCities = this.GetSwiftCities.bind(this);
    this.GetSwiftBanks = this.GetSwiftBanks.bind(this);
    this.GetBopCodes = this.GetBopCodes.bind(this);
    this.GetVocCodes = this.GetVocCodes.bind(this);
    this.DealerRateExecute = this.DealerRateExecute.bind(this);
    this.GetDealerRates = this.GetDealerRates.bind(this);
    this.InitiateRequestDealerTransfer = this.InitiateRequestDealerTransfer.bind(this);
    this.GetSwiftCodesByBic = this.GetSwiftCodesByBic.bind(this);
    this.RejectDealerRate = this.RejectDealerRate.bind(this);
    this.RequestNewDealerRate = this.RequestNewDealerRate.bind(this);
    this.InitiateCurrentDealerRate = this.InitiateCurrentDealerRate.bind(this);
    this.ExecuteWithCurrentDealerRate = this.ExecuteWithCurrentDealerRate.bind(this);
    this.GetTransferValueDate = this.GetTransferValueDate.bind(this);
    this.GetSwiftCountries = this.GetSwiftCountries.bind(this);
    this.GetCurrencySearch = this.GetCurrencySearch.bind(this);
    this.GetOrderCharges = this.GetOrderCharges.bind(this);
    this.ForecastRouting = this.ForecastRouting.bind(this);
  }

  Routing(
    { debitAccountNumber, creditAccountNumber, orderCurrency, orderAmount, creditAccountType, isIban }: Routing,
    nextCallback: SuccessCallback,
  ): Promise<RoutingResponseBody> {
    return this.itemRepo.Routing(
      {
        debitAccountNumber,
        creditAccountNumber,
        orderCurrency,
        orderAmount,
        creditAccountType,
        isIban,
      },
      nextCallback,
    );
  }

  Forecast(data: Forecast, nextCallback: SuccessCallback): Promise<ForecastResponseBody> {
    return this.itemRepo.Forecast(data, nextCallback);
  }

  Vop(data: Vop, nextCallback: SuccessCallback): Promise<VopResponseBody> {
    return this.itemRepo.Vop(data, nextCallback);
  }

  Initiate(data: InitiateTransferRequest, nextCallback: SuccessCallback): Promise<InitiateTransferResponseBody> {
    return this.itemRepo.Initiate(data, nextCallback);
  }

  ExchangeCalculate(data: ExchangeCalculate, nextCallback?: SuccessCallback): Promise<ExchangeCalculateResponseBody> {
    return this.itemRepo.ExchangeCalculate(data, nextCallback);
  }

  ExchangeRates(data?: ExchangeRates, nextCallback?: SuccessCallback): Promise<ExchangeRatesResponseBody> {
    return this.itemRepo.ExchangeRates(data, nextCallback);
  }

  GetDocuments(data: { transactionId: string }): Promise<Document[]> {
    return this.itemRepo.GetDocuments(data);
  }

  PostDocument(data: PostDocument & { nextCallback?: SuccessCallback }, nextCallback?: SuccessCallback): Promise<any> {
    return this.itemRepo.PostDocument(data, nextCallback);
  }

  DeleteDocument(
    data: DeleteDocument & { nextCallback?: SuccessCallback },
    nextCallback?: SuccessCallback,
  ): Promise<any> {
    return this.itemRepo.DeleteDocument(data, nextCallback);
  }

  MakeTransfer(data: { requestId: string }): Promise<MakeTransferResponseBody> {
    return this.itemRepo.MakeTransfer(data);
  }

  GetBankSwiftCodes(data: { cityName: string; countryName: string; bankName: string }): Promise<SwiftCode[]> {
    return this.itemRepo.GetBankSwiftCodes(data);
  }

  GetSwiftCities(data: { countryName: string }): Promise<SwiftCity[]> {
    return this.itemRepo.GetSwiftCities(data);
  }

  GetSwiftBanks(data: { countryName: string; cityName: string }): Promise<SwiftBank[]> {
    return this.itemRepo.GetSwiftBanks(data);
  }

  GetBopCodes(data: { selectedCountryId: number }): Promise<BopCode[]> {
    let bopCode = BopCodesIdentifiers.BOPUA;
    switch (data.selectedCountryId) {
      case 74:
        bopCode = BopCodesIdentifiers.POPUK;
        break;
      case 76:
        bopCode = BopCodesIdentifiers.BOPIN;
        break;
      case 78:
        bopCode = BopCodesIdentifiers.BOPJO;
        break;
      default:
        break;
    }
    return this.itemRepo.GetBopCodes(bopCode);
  }

  GetVocCodes(): Promise<VocCode[]> {
    return this.itemRepo.GetVocCodes();
  }
  DealerRateExecute(
    data: DealerRateExecute,
    nextCallback?: SuccessCallback | undefined,
  ): Promise<DealerRateExecuteResponseBody> {
    return this.itemRepo.DealerRateExecute(data, nextCallback);
  }

  GetDealerRates(params: { referenceNumber: string; dealerUserCode: string }): Promise<DealerRatesWithCalculatedTimer> {
    return this.itemRepo.GetDealerRates(params.referenceNumber, params.dealerUserCode);
  }

  InitiateRequestDealerTransfer(data: {
    orderCode: string;
    appTransactionId: number;
    nextCallback?: SuccessCallback;
  }): Promise<InitiateTransferResponseBody> {
    return this.itemRepo.InitiateRequestDealerTransfer(data.orderCode, data.appTransactionId, data.nextCallback);
  }

  GetSwiftCodesByBic(data: { bic: string; nextCallback?: SuccessCallback }): Promise<SwiftCode> {
    return this.itemRepo.GetSwiftCodesByBic(data);
  }

  RejectDealerRate(data: { orderCode: string; nextCallback?: SuccessCallback | undefined }): Promise<{}> {
    return this.itemRepo.RejectDealerRate(data.orderCode, data?.nextCallback);
  }

  RequestNewDealerRate(data: { orderCode: string; nextCallback?: SuccessCallback | undefined }): Promise<{}> {
    return this.itemRepo.RequestNewDealerRate(data.orderCode, data?.nextCallback);
  }

  InitiateCurrentDealerRate(data: {
    orderCode: string;
    appTransactionId: number;
    nextCallback?: SuccessCallback | undefined;
  }): Promise<InitiateTransferResponseBody> {
    return this.itemRepo.InitiateCurrentDealerRate(data.orderCode, data.appTransactionId, data?.nextCallback);
  }

  ExecuteWithCurrentDealerRate(data: {
    requestId: string;
    nextCallback?: SuccessCallback | undefined;
  }): Promise<DealerRateExecuteResponseBody> {
    return this.itemRepo.ExecuteWithCurrentDealerRate(data.requestId, data?.nextCallback);
  }

  GetTransferValueDate(data: {
    priorityDate: Priorities;
    nextCallback?: SuccessCallback | undefined;
    onErrorCallback?: ErrorCallback | undefined;
  }): Promise<ValueDateResponseBody> {
    return this.itemRepo.GetTransferValueDate(data.priorityDate, data?.nextCallback, data?.onErrorCallback);
  }

  GetSwiftCountries(): Promise<SwiftCountry[]> {
    return this.itemRepo.GetSwiftCountries();
  }

  GetCurrencySearch(data: CurrencySearch): Promise<CurrencySearchResponseBody> {
    return this.itemRepo.GetCurrencySearch(data);
  }

  GetOrderCharges(params: { orderCode: string }): Promise<OrderChargesResponseBody> {
    return this.itemRepo.GetOrderCharges(params.orderCode);
  }

  ForecastRouting(data: {
    debitAccount: string;
    creditAccount: string;
    bic: string;
    amount: number;
    currency: number;
    charges: number;
    chargesDescription: string;
  }): Promise<ForecastRoutingResponseBody> {
    return this.itemRepo.ForecastRouting(
      data.debitAccount,
      data.creditAccount,
      data.bic,
      data.amount,
      data.currency,
      data.charges,
      data.chargesDescription,
    );
  }
}
